import React, { useState } from 'react';
import { Box, Popover, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TagAutoComplete from '../../common/TagAutoComplete';
import { tagService } from '../../../services';
import type { UnifiedTag, CreateTagRequest } from '../../../services/content/tag.service';
import type { TagScope } from '../../../types/content.types';

export interface AddTagsPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** Called once with the full list of tag IDs when the user closes / submits the popover. */
  onApply: (tagIds: string[]) => void;
  /** Heading text inside the popover (e.g. "Add tags to 3 files" or "Add tags"). */
  heading: string;
  /** When true, show the "merged with AI suggestions" note. */
  showMergeNote?: boolean;
  /** Available tags loaded from the team, passed down from ContentUpload. */
  availableTags: UnifiedTag[];
  organizationId: string;
  teamId: string;
  userId: string;
  /** Called when the user creates a new tag so the parent can add it to its own list. */
  onTagCreated?: (tag: UnifiedTag) => void;
}

export const AddTagsPopover: React.FC<AddTagsPopoverProps> = ({
  anchorEl,
  onClose,
  onApply,
  heading,
  showMergeNote,
  availableTags,
  organizationId,
  teamId,
  userId,
  onTagCreated,
}) => {
  const [draft, setDraft] = useState<string[]>([]);
  // Mirror of availableTags that grows when the user creates a new tag in this session.
  const [localTags, setLocalTags] = useState<UnifiedTag[]>(availableTags);

  // Keep localTags in sync when the parent refreshes availableTags (e.g. after mount).
  React.useEffect(() => {
    setLocalTags(availableTags);
  }, [availableTags]);

  const commit = () => {
    if (draft.length) onApply(draft);
    setDraft([]);
    onClose();
  };

  const handleCreateTag = async (request: CreateTagRequest): Promise<UnifiedTag> => {
    const newTag = await tagService.createUnifiedTag({
      ...request,
      scope: 'team' as TagScope,
      organization_id: organizationId,
      team_id: teamId,
      owner_id: teamId,
    });
    // Add to local list so the chip renders immediately without a round-trip.
    setLocalTags((prev) => [...prev, newTag]);
    onTagCreated?.(newTag);
    return newTag;
  };

  return (
    <Popover
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={commit}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{ sx: { p: 2, width: 360 } }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '.08em',
          color: 'text.secondary',
          display: 'block',
          mb: 1,
        }}
      >
        {heading}
      </Typography>

      <TagAutoComplete
        appliedTagIds={draft}
        availableTags={localTags}
        onTagApply={(tagId) => setDraft((d) => (d.includes(tagId) ? d : [...d, tagId]))}
        onTagRemove={(tagId) => setDraft((d) => d.filter((x) => x !== tagId))}
        onCreateTag={handleCreateTag}
        tagScope={'team' as TagScope}
        organizationId={organizationId}
        teamId={teamId}
        userId={userId}
        placeholder="Type to search, / to browse"
        label=""
        maxTags={20}
      />

      {showMergeNote && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 1 }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 12 }} />
          These will be added on top of any AI-suggested tags.
        </Typography>
      )}
    </Popover>
  );
};
